import { Shield, TrendingUp, TrendingDown, Minus, CheckCircle, Users, Building2, Scale, Heart, GraduationCap, Briefcase } from 'lucide-react';

// SAFEScore Gauge Display Component
export const SAFEScoreGauge = ({ score, label, size = 'large' }) => {
  const getScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-500';
    if (score >= 60) return 'text-cyan-600';
    if (score >= 40) return 'text-amber-500';
    return 'text-red-500';
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Fair';
    return 'Needs Improvement';
  };

  const sizeClasses = size === 'large' 
    ? 'w-32 h-32 text-4xl' 
    : 'w-20 h-20 text-2xl';

  return (
    <div className="flex flex-col items-center">
      <div className={`${sizeClasses} rounded-full border-4 border-slate-200 flex items-center justify-center relative bg-white`}>
        <div className={`font-bold ${getScoreColor(score)}`}>{score}</div>
        <div className="absolute -bottom-1 bg-slate-900 text-white text-xs px-2 py-0.5 uppercase tracking-wider">
          {getScoreLabel(score)}
        </div>
      </div>
      {label && <p className="text-sm text-slate-600 mt-4 font-medium">{label}</p>}
    </div>
  );
};

// Platform Card with SAFEScore
export const PlatformScoreCard = ({ platform, rank }) => {
  const getTrendIcon = (trend) => {
    if (trend === 'up') return <TrendingUp className="w-4 h-4 text-emerald-500" />;
    if (trend === 'down') return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-slate-400" />;
  };

  const getCertBadge = (level) => {
    const badges = {
      'endorsed': { bg: 'bg-amber-600', text: 'SAFE-Endorsed' },
      'certified': { bg: 'bg-cyan-700', text: 'SAFE-Certified' },
      'ready': { bg: 'bg-slate-600', text: 'SAFE-Ready' },
      'pending': { bg: 'bg-slate-300 text-slate-700', text: 'Pending Review' }
    };
    return badges[level] || badges['pending'];
  };

  const badge = getCertBadge(platform.certLevel);

  return (
    <div className="bg-white border border-slate-200 p-6 hover:border-slate-400 transition-all" data-testid={`platform-card-${platform.id}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 bg-slate-900 text-white flex items-center justify-center font-bold text-lg">
            {rank}
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">{platform.name}</h3>
            <p className="text-sm text-slate-500">{platform.category}</p>
          </div>
        </div>
        <span className={`${badge.bg} text-white text-xs px-3 py-1 font-semibold uppercase tracking-wider`}>
          {badge.text}
        </span>
      </div>
      
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-6">
          <div className="text-center">
            <p className="text-3xl font-bold text-slate-900">{platform.score}</p>
            <p className="text-xs text-slate-500 uppercase tracking-wider">SAFEScore</p>
          </div>
          <div className="flex items-center gap-1">
            {getTrendIcon(platform.trend)}
            <span className="text-sm text-slate-600">{platform.change}</span>
          </div>
        </div>
        <div className="text-right">
          <p className="text-sm text-slate-600">{platform.lastAudit}</p>
          <p className="text-xs text-slate-400">Last Audit</p>
        </div>
      </div>
    </div>
  );
};

// Benefits Grid for All Stakeholders
export const StakeholderBenefits = () => {
  const benefits = [
    {
      icon: Users,
      stakeholder: 'Parents & Families',
      color: 'bg-cyan-700',
      benefits: [
        'Instant visibility into platform safety',
        'Make informed choices for children',
        'Real-time alerts on score changes',
        'Compare platforms before allowing access'
      ]
    },
    {
      icon: Building2,
      stakeholder: 'Technology Platforms',
      color: 'bg-amber-600',
      benefits: [
        'Trustpilot-style trust signal for users',
        'Competitive differentiation',
        'Reduced liability with certification',
        'Premium positioning in rankings'
      ]
    },
    {
      icon: Scale,
      stakeholder: 'Regulators',
      color: 'bg-slate-900',
      benefits: [
        'Independent compliance verification',
        'Real-time monitoring dashboard',
        'Evidence-based enforcement',
        'Standardized safety metrics'
      ]
    },
    {
      icon: GraduationCap,
      stakeholder: 'Schools & Educators',
      color: 'bg-emerald-600',
      benefits: [
        'Procurement decision support',
        'Due diligence documentation',
        'Parent communication tool',
        'Liability risk reduction'
      ]
    },
    {
      icon: Briefcase,
      stakeholder: 'Insurance & Risk',
      color: 'bg-purple-600',
      benefits: [
        'Actuarial risk signals',
        'Premium calculation inputs',
        'Claims prediction data',
        'Portfolio risk assessment'
      ]
    },
    {
      icon: Heart,
      stakeholder: 'Child Protection Orgs',
      color: 'bg-red-600',
      benefits: [
        'Advocacy evidence base',
        'Platform accountability tool',
        'Policy recommendation data',
        'Public awareness campaigns'
      ]
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="stakeholder-benefits">
      {benefits.map((item, index) => (
        <div key={index} className="bg-white border border-slate-200 overflow-hidden">
          <div className={`${item.color} text-white p-4 flex items-center gap-3`}>
            <item.icon className="w-6 h-6" />
            <h3 className="font-semibold">{item.stakeholder}</h3>
          </div>
          <div className="p-4">
            <ul className="space-y-2">
              {item.benefits.map((benefit, bIndex) => (
                <li key={bIndex} className="flex items-start gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-cyan-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ))}
    </div>
  );
};

// SAFEScore Explainer
export const SAFEScoreExplainer = () => {
  const factors = [
    { name: 'Detection Accuracy', weight: '25%', desc: 'How accurately the platform detects harmful patterns' },
    { name: 'Response Time', weight: '20%', desc: 'Speed of intervention when threats are detected' },
    { name: 'False Positive Rate', weight: '15%', desc: 'Minimizing incorrect flagging of safe content' },
    { name: 'Governance Compliance', weight: '20%', desc: 'Adherence to SAFE governance standards' },
    { name: 'Audit Performance', weight: '20%', desc: 'Results from SAFE Labs certification audits' }
  ];

  return (
    <div className="bg-slate-50 border border-slate-200 p-8" data-testid="safescore-explainer">
      <div className="flex items-center gap-3 mb-6">
        <Shield className="w-8 h-8 text-cyan-700" />
        <h3 className="text-xl font-semibold text-slate-900">How SAFEScore Works</h3>
      </div>
      
      <p className="text-slate-600 mb-8">
        Like Trustpilot for e-commerce or BBB ratings in the USA, the SAFEScore provides an independent, real-time measure of a platform's child safeguarding effectiveness.
      </p>

      <div className="space-y-4">
        {factors.map((factor, index) => (
          <div key={index} className="flex items-center gap-4">
            <div className="w-16 text-right">
              <span className="font-bold text-cyan-700">{factor.weight}</span>
            </div>
            <div className="flex-1 bg-white border border-slate-200 p-4">
              <p className="font-medium text-slate-900">{factor.name}</p>
              <p className="text-sm text-slate-500">{factor.desc}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 bg-slate-900 text-white p-6">
        <p className="text-amber-400 font-semibold mb-2">Real-Time Updates</p>
        <p className="text-slate-300 text-sm">
          Unlike annual audits, SAFEScores update continuously based on platform performance, giving stakeholders always-current visibility into safeguarding effectiveness.
        </p>
      </div>
    </div>
  );
};

// Live Rankings Preview
export const LiveRankingsPreview = () => {
  // Sample data - in production this would come from API
  const samplePlatforms = [
    { id: 1, name: 'Platform Alpha', category: 'Social Media', score: 87, trend: 'up', change: '+3', certLevel: 'endorsed', lastAudit: 'Mar 2025' },
    { id: 2, name: 'Platform Beta', category: 'EdTech', score: 82, trend: 'up', change: '+5', certLevel: 'certified', lastAudit: 'Mar 2025' },
    { id: 3, name: 'Platform Gamma', category: 'Gaming', score: 74, trend: 'stable', change: '0', certLevel: 'certified', lastAudit: 'Feb 2025' },
    { id: 4, name: 'Platform Delta', category: 'Messaging', score: 68, trend: 'down', change: '-2', certLevel: 'ready', lastAudit: 'Feb 2025' },
    { id: 5, name: 'Platform Epsilon', category: 'Video', score: 61, trend: 'up', change: '+4', certLevel: 'ready', lastAudit: 'Jan 2025' },
  ];

  return (
    <div data-testid="live-rankings-preview">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">Live Rankings</p>
          <h3 className="text-2xl font-semibold text-slate-900">Platform SAFEScores</h3>
        </div>
        <div className="flex items-center gap-2 bg-emerald-50 text-emerald-700 px-4 py-2">
          <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
          <span className="text-sm font-medium">Real-time</span>
        </div>
      </div>

      <div className="space-y-4">
        {samplePlatforms.map((platform, index) => (
          <PlatformScoreCard key={platform.id} platform={platform} rank={index + 1} />
        ))}
      </div>

      <div className="mt-6 text-center">
        <p className="text-sm text-slate-500 italic">
          * Sample data shown. Live rankings will display certified platforms upon launch.
        </p>
      </div>
    </div>
  );
};
